import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireSeason, type TreadWearRecord } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiButton, NoddiCard } from 'noddi-ui-web';
import type { TreadDepthThresholds } from 'noddi-util';
import { TreadDepthMeter, TreadDepthMeterLegend } from 'noddi-web';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { formatMm } from './formatMm';
import { ReadWhyButton } from './ReadWhyButton';
import { RiskIcon, RiskList } from './RiskList';
import { StatusBand, type StatusBandPoint } from './StatusBand';
import { getTirePositionLabel } from './tireLeadLabels';
import type { TireLeadReasonCopy, TireLeadSeverity } from './tireLeadReasonCopy';
import { isTireSeasonNow } from './tireSeasonNow';

// Red risk tiles, as in the design, though the badge says "should be replaced" (medium): the risks of worn tread are
// the dangerous ones, braking and aquaplaning, while the offer itself is not urgent.
const RISK_TONE: TireLeadSeverity = 'high';

type Props = {
  treadWear: TreadWearRecord;
  thresholds: TreadDepthThresholds | null;
  copy: TireLeadReasonCopy;
  season: TireSeason;
  belowLegalLimit: boolean;
  onOpenWhy: () => void;
  /** Missing when the customer can no longer set a reminder. */
  onOpenRemind?: () => void;
};

/** Worn tread: how much is left on the most worn tire, what that risks, and what the new tires come with. */
export const WornTreadStatusCard = ({
  treadWear,
  thresholds,
  copy,
  season,
  belowLegalLimit,
  onOpenWhy,
  onOpenRemind
}: Props) => {
  const point = getClosingPoint(season, belowLegalLimit, thresholds);

  return (
    <NoddiCard>
      <div className='flex flex-col gap-5'>
        <div className='flex items-end justify-between gap-4'>
          <Measurement treadWear={treadWear} belowLegalLimit={belowLegalLimit} />
          {thresholds && (
            <div className='flex flex-col items-end text-xs text-muted-foreground'>
              <Trans>Recommended minimum</Trans>
              <span className='text-base font-semibold text-foreground'>{formatMm(thresholds.recommendedMm)} mm</span>
            </div>
          )}
        </div>
        <Meter treadWear={treadWear} thresholds={thresholds} />
        <RiskList risks={copy.risks} severity={RISK_TONE}>
          {belowLegalLimit ? (
            <div className='flex items-start gap-3 rounded-lg bg-danger50 p-3.5 text-dangerDark'>
              <NoddiIcon name={point.icon} size='xMedium' className='flex-none' />
              <div className='flex flex-col gap-1'>
                <span className='font-bold'>{point.title}</span>
                <span className='text-sm leading-relaxed'>{point.description}</span>
              </div>
            </div>
          ) : (
            <div className='flex items-center gap-3'>
              <RiskIcon icon={point.icon} severity='info' />
              <span className='font-medium'>{point.title}</span>
            </div>
          )}
          <ReadWhyButton onClick={onOpenWhy} />
        </RiskList>
        {onOpenRemind && (
          <NoddiButton variant='outline' startIcon='Bell' className='w-full' onClick={onOpenRemind}>
            <Trans>Remind me later</Trans>
          </NoddiButton>
        )}
      </div>
    </NoddiCard>
  );
};

/** The same status across the desktop page: the measurement, then the two risks and what the new tires come with. */
export const WornTreadStatusBand = ({
  treadWear,
  thresholds,
  copy,
  season,
  belowLegalLimit,
  onOpenWhy
}: Omit<Props, 'onOpenRemind'>) => {
  const points: StatusBandPoint[] = [
    ...copy.whyPoints.slice(0, 2).map((whyPoint, index) => ({
      icon: index === 0 ? ('Droplet' as const) : ('TriangleAlert' as const),
      ...whyPoint
    })),
    getClosingPoint(season, belowLegalLimit, thresholds)
  ];

  return (
    <StatusBand points={points} severity={RISK_TONE}>
      <Measurement treadWear={treadWear} belowLegalLimit={belowLegalLimit} />
      <Meter treadWear={treadWear} thresholds={thresholds} />
      <ReadWhyButton onClick={onOpenWhy} />
    </StatusBand>
  );
};

const Measurement = ({ treadWear, belowLegalLimit }: { treadWear: TreadWearRecord; belowLegalLimit: boolean }) => (
  <div className='flex flex-col gap-1'>
    <span className={cn('text-5xl leading-none font-bold tracking-tight', belowLegalLimit && 'text-dangerDark')}>
      {formatMm(getLatestMm(treadWear))}
      <span className='text-xl font-semibold text-muted-foreground'> mm</span>
    </span>
    <span className='text-sm text-muted-foreground'>
      <Trans>left on your most worn tire</Trans>
    </span>
  </div>
);

const Meter = ({ treadWear, thresholds }: Pick<Props, 'treadWear' | 'thresholds'>) => (
  <div className='flex flex-col gap-3'>
    <TreadDepthMeter
      valueMm={getLatestMm(treadWear)}
      thresholds={thresholds}
      label={getTirePositionLabel(treadWear.position)}
      formatNumber={formatMm}
    />
    {thresholds && (
      <TreadDepthMeterLegend
        thresholds={thresholds}
        translations={getCommonTranslations().wheelMeasurements}
        formatNumber={formatMm}
      />
    )}
  </div>
);

/**
 * The point after the risks: below the legal limit, that driving on them is illegal; otherwise what the new tires
 * come with, fitted on the wheel set ahead of the season, or brought to the customer in season.
 */
const getClosingPoint = (
  season: TireSeason,
  belowLegalLimit: boolean,
  thresholds: TreadDepthThresholds | null
): StatusBandPoint => {
  if (belowLegalLimit && thresholds) {
    const limitMm = formatMm(thresholds.limitMm);
    return {
      icon: 'CircleAlert',
      title: t`It is illegal to drive on these tires`,
      description:
        season === TireSeason.WINTER
          ? t`The tread is below the legal limit of ${limitMm} mm for winter tires. You risk a fine, and your insurer can reduce the payout after an accident.`
          : t`The tread is below the legal limit of ${limitMm} mm. You risk a fine, and your insurer can reduce the payout after an accident.`
    };
  }
  if (!isTireSeasonNow(season)) {
    return {
      icon: 'Calendar',
      severity: 'info',
      title:
        season === TireSeason.WINTER
          ? t`Ready for when you switch to winter tires`
          : t`Ready for when you switch to summer tires`,
      description: t`We fit the new tires on your wheel set now, so they are ready when the season starts.`
    };
  }
  return {
    icon: 'Home',
    severity: 'info',
    title: t`Fixed price, and we come to you`,
    description: t`You only pay for the tires and the fitting. We change the tires at your home or at work.`
  };
};

const getLatestMm = (treadWear: TreadWearRecord) => treadWear.readings.at(-1)?.valueMm ?? 0;
